import {
  FunctionField,
  List,
  useGetIdentity,
  useListContext,
  Link,
  Pagination,
  useGetOne,
  ListContextProvider,
  useGetList,
  BulkDeleteButton,
  TextInput,
  FilterForm,
} from "react-admin";
import { Box, Avatar, Stack, Typography, CircularProgress, Link as MuiLink } from "@mui/material";
import { useState, useMemo, Dispatch, SetStateAction } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { TaskDetailsDialog } from "./components/task-dialogs/TaskDetailsDialog";
import { TaskCreateDialog } from "./components/task-dialogs/TaskCreateDialog";
import { PageContent, TabContent } from "@/components/common/PageLayout";
import { StyledTab, StyledTabs } from "@/components/ui/mui/StyledTabs";
import Button from "@/components/ui/Button";
import { Add as AddIcon } from "@mui/icons-material";
import { stringToColor } from "@/components/common/fields/AvatarField";
import { ResourceName, TaskStatus, User, ValidAny, type Task } from "@/types";
import { getTasksBySelectedTab, TaskTab, getInitials } from "./utils/taskUtils";
import { StyledDatagrid } from "@/components/ui/react-admin/StyledDatagrid";
import { SectionCard } from "@/components/common/SectionCard";
import { getResourceIcon } from "@/utils/icons";
import { EditableTaskStatusField } from "@/resources/tasks/components/EditableTaskStatusField";
import { convertCamelCaseToTitleCase } from "@/utils/formatters/stringFormatters";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { ButtonStyleType } from "@/types/shared/uiComponents";
import { DueBadge } from "./components/shared/DueBadge";
import { TaskStats } from "./components/task-list/TaskStats";
import { ListEmptyState } from "@/components/common/ListEmptyState";
import { taskRecordPath } from "./utils/taskRecordLink";

const taskFilters = [<TextInput label="Search" source="q" alwaysOn fullWidth />];

// Custom field for assigned user with avatar
export const AssignedUserField = ({ record }: { record: Task }) => {
  const { data: user, isLoading } = useGetOne(
    ResourceName.USERS,
    { id: record.assigned_user_id_ref },
    { enabled: !!record.assigned_user_id_ref, retry: false }
  );

  if (!record.assigned_user_id_ref) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unassigned
      </Typography>
    );
  }

  if (isLoading) return <CircularProgress size={16} />;

  if (!user) {
    return (
      <Typography variant="body2" color="text.secondary">
        Unknown
      </Typography>
    );
  }

  const displayName = user.full_name || user.email || "Unknown";
  const { bgColor, textColor } = stringToColor(displayName);

  return (
    <Stack direction="row" alignItems="center" spacing={1}>
      <Avatar
        sx={{
          width: 22,
          height: 22,
          fontSize: 10,
          bgcolor: bgColor,
          color: textColor,
          flexShrink: 0,
        }}
      >
        {getInitials(displayName)}
      </Avatar>
      <Typography variant="body2" noWrap sx={{ maxWidth: 140 }}>
        {displayName}
      </Typography>
    </Stack>
  );
};

// Custom field for related record link
const RelatedRecordField = ({ record }: { record: Task }) => {
  if (!record.record?.object_type || !record.record?.object_id) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  return (
    <MuiLink
      component={Link}
      to={taskRecordPath(record) ?? ""}
      underline="hover"
      color="primary"
      onClick={(e: React.MouseEvent) => e.stopPropagation()}
      sx={{ fontSize: 14 }}
    >
      {record.record.label || `#${record.record.object_id}`}
    </MuiLink>
  );
};

export const TaskList = () => {
  const { identity } = useGetIdentity();
  const userId = identity?.id as string | undefined;

  const { canCreate, canDelete, hasPartnerList } = useResourcePermissions(ResourceName.TASKS);

  const [tab, setTab] = useState<TaskTab>(TaskTab.MY_TASKS);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);

  const TasksIcon = getResourceIcon(ResourceName.TASKS);

  // Filter tabs based on permissions — admin-only tabs require partner-wide (unrestricted) delete scope
  const visibleTabs = useMemo(() => {
    const adminOnlyTabs = [TaskTab.ALL_TASKS, TaskTab.UNASSIGNED];
    return Object.values(TaskTab).filter((tabValue) => {
      if (adminOnlyTabs.includes(tabValue)) {
        return hasPartnerList;
      }
      return true;
    });
  }, [hasPartnerList]);

  // If current tab is no longer visible, reset to MY_TASKS
  useMemo(() => {
    if (!visibleTabs.includes(tab)) {
      setTab(TaskTab.MY_TASKS);
    }
  }, [visibleTabs, tab]);

  // Build filter based on tab — MY_TASKS uses assigned_user_id_ref instead of nested route
  const filter = useMemo(() => {
    const base = { status_in: [TaskStatus.OPEN, TaskStatus.IN_PROGRESS, TaskStatus.COMPLETED] };
    if (tab === TaskTab.MY_TASKS && userId) {
      return { ...base, assigned_user_id_ref: userId };
    }
    return base;
  }, [tab, userId]);

  return (
    <>
      <PageHeader
        customTitle="Tasks"
        customSubtitle="View and manage tasks by assignment, due date, and record."
        hideSectionTitle
        rightContent={
          canCreate && (
            <Button startIcon={<AddIcon />} variant={ButtonStyleType.Text} onClick={() => setOpenCreateDialog(true)}>
              Create Task
            </Button>
          )
        }
        headerIcon={<TasksIcon sx={{ fontSize: 28, color: "primary.main" }} />}
      />

      <PageContent>
        <StyledTabs
          value={tab}
          onChange={(_, newValue) => setTab(newValue)}
          textColor="primary"
          indicatorColor="primary"
          sx={{ mt: 3, mb: 3 }}
        >
          {visibleTabs.map((label) => (
            <StyledTab key={label} label={convertCamelCaseToTitleCase(label)} value={label} />
          ))}
        </StyledTabs>

        <TabContent>
          <List
            resource={ResourceName.TASKS}
            filter={filter}
            sort={{ field: "due_date", order: "ASC" }}
            perPage={25}
            actions={false}
            component="div"
            pagination={<Pagination rowsPerPageOptions={[10, 25, 50, 100]} />}
            empty={
              <ListEmptyState
                resource={ResourceName.TASKS}
                iconSize={80}
                action={
                  canCreate && (
                    <Button startIcon={<AddIcon />} onClick={() => setOpenCreateDialog(true)}>
                      Create Task
                    </Button>
                  )
                }
              />
            }
          >
            <TaskDatagridWithStats
              setSelectedTask={setSelectedTask}
              setOpenTaskDialog={setOpenTaskDialog}
              tab={tab}
              canDelete={canDelete}
            />
          </List>
        </TabContent>
      </PageContent>

      {selectedTask && (
        <TaskDetailsDialog task={selectedTask} open={openTaskDialog} onClose={() => setOpenTaskDialog(false)} />
      )}

      {canCreate && <TaskCreateDialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)} />}
    </>
  );
};

interface TaskDatagridWithStatsProps {
  setSelectedTask: Dispatch<SetStateAction<Task | null>>;
  setOpenTaskDialog: Dispatch<SetStateAction<boolean>>;
  tab: TaskTab;
  canDelete: boolean;
}

const TaskDatagridWithStats = ({ setSelectedTask, setOpenTaskDialog, tab, canDelete }: TaskDatagridWithStatsProps) => {
  const listContext = useListContext<Task>();
  const { data: allTasks = [] } = listContext;

  const { data: users = [] } = useGetList<User>(ResourceName.USERS, {
    pagination: { page: 1, perPage: 1000 },
    sort: { field: "full_name", order: "ASC" },
  });

  const validUserIds = useMemo(() => users.map((user: User) => user.id), [users]);

  const tasks = useMemo(() => {
    const searchQuery = listContext.filterValues?.q?.toLowerCase() || "";
    return getTasksBySelectedTab(allTasks, tab, validUserIds, searchQuery);
  }, [allTasks, tab, validUserIds, listContext.filterValues]);

  const modifiedListContext = useMemo(
    () => ({
      ...listContext,
      data: tasks,
      total: tasks.length,
    }),
    [listContext, tasks]
  );

  return (
    <>
      <Box pb={4}>
        <TaskStats tab={tab} validUserIds={validUserIds} />
      </Box>

      <SectionCard>
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
          <FilterForm filters={taskFilters} />
        </Box>
        <ListContextProvider value={modifiedListContext as ValidAny}>
          <StyledDatagrid
            rowClick={(_id, _resource, record) => {
              setSelectedTask(record as Task);
              setOpenTaskDialog(true);
              return false;
            }}
            bulkActionButtons={canDelete ? <BulkDeleteButton /> : false}
          >
            <FunctionField
              label="Title"
              source="title"
              render={(record: Task) => (
                <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 280 }}>
                  {record.title}
                </Typography>
              )}
            />

            <EditableTaskStatusField resource={ResourceName.TASKS} label="Status" />

            <FunctionField
              label="Related To"
              source="record"
              sortable={false}
              render={(record: Task) => <RelatedRecordField record={record} />}
            />

            <FunctionField
              label="Assigned To"
              source="assigned_user_id_ref"
              render={(record: Task) => <AssignedUserField record={record} />}
            />

            <FunctionField
              label="Due"
              source="due_date"
              render={(record: Task) => <DueBadge dueDate={record.due_date} />}
            />
          </StyledDatagrid>
        </ListContextProvider>
      </SectionCard>
    </>
  );
};
